/**
 * TableCSV is a React functional function that renders a table with pagination.
 * The table displays data provided in the `rows` array, which can be filled using the `fillArray` function.
 * The table uses Material-UI components for styling and layout.
 *
 * @function
 * @example
 * // Example usage:
 * // Import the function and the fillArray function
 * import { TableCSV, fillArray } from './Table';
 * 
 * // Fill the table with data
 * fillArray([{ scientificName: 'Homo sapiens' }, { scientificName: 'Pan troglodytes' }]);
 * 
 * // Render the function
 * <TableCSV />
 *
 * @returns {TSX.Element} The rendered table function.
 */

import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';


interface Column {
  id: 'scientificName';
  label: string;
  minWidth?: number;
  align?: 'right';
}

interface Row {
  id?: number;
  scientificName: string;
}

const columns: readonly Column[] = [
    { id: 'scientificName', label: 'scientificName', minWidth: 170},
];

var rows: Row[] = [{"scientificName":""}];

export  function fillArray(csvData:Array<{"scientificName":""}>) {
  rows = csvData;
}

export function TableCSV({setImportTime}: any) {
    const { t } = useTranslation();

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_event: unknown, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    };

    function getEstimationText(rowCount: number) {
      const url = process.env.REACT_APP_LINNAE_URL;
      // En prod : 1 min pour 1000 lignes
      let ratio = 1 / 1000;
      // En local/dev : temps multiplié par 6.25
      if (
        url === "http://localhost:3000/" ||
        url === "https://l4e.b5e.dev/"
      ) {
        ratio *= 6.25;
      }

      const minutes = Math.ceil(rowCount * ratio);

      setImportTime(minutes);

      if (minutes <= 1) return (t("estimation-text-1") + 1 + t("estimation-text-4"));
      if (minutes <= 5) return (t("estimation-text-2") + 5 + t("estimation-text-4"));
      if (minutes <= 10) return (t("estimation-text-2") + 10 + t("estimation-text-4"));
      return (t("estimation-text-3") + 10 + t("estimation-text-4"));
    }

    const estimationText = getEstimationText(rows.length);

    return (
      <>
      <Paper sx={{ width: '100%', overflow: 'hidden', mt: 1, mb: 1 }}>
        <TableContainer sx={{ maxHeight: 240 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      fontWeight: 'bold',
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(row => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell 
                            key={column.id}
                            align={column.align}
                          >
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ marginTop: 8, marginBottom: 8, marginLeft: 12, fontStyle: "italic", color: "#666" }}>
            {t('estimation-text')} {estimationText}
          </div>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
        </Paper>
      </>
    );
}
